import { Button } from '@ver-uds/react';
import { Alert, TextInputControl } from '@ver-uds/uswds-react';
import React from 'react';
import { SubmitHandler, UseFormMethods } from 'react-hook-form';
import { OTCErrors, UserAccountForm } from '../utils/constants';

const emailValidationRegex = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export const NewUserAccountEve = ({
  formMethods,
  onSubmit,
  submissionError,
  isLoading,
  authErrorCode,
}: {
  formMethods: UseFormMethods<UserAccountForm>;
  onSubmit: SubmitHandler<UserAccountForm>;
  submissionError: boolean;
  isLoading: boolean;
  authErrorCode: string | null;
}): JSX.Element => {
  // eslint-disable-next-line @typescript-eslint/unbound-method
  const {
    formState: { errors },
    handleSubmit,
    register,
  } = formMethods;

  // Change OTC validation error alert messaging based on error code
  let isOTCExpired = false;
  if (authErrorCode?.includes(OTCErrors.TIMEOUT) || authErrorCode?.includes(OTCErrors.ALREADY_VERIFIED)) {
    isOTCExpired = true;
  }

  return (
    <section>
      {authErrorCode && (
        <Alert status="error">
          <Alert.Heading>{`${isOTCExpired ? 'Expired' : 'Invalid'} Activation Link `}</Alert.Heading>
          <Alert.Text>{`The link you used is ${
            isOTCExpired ? 'no longer' : 'not'
          } valid. Enter your email address below to generate a new link.`}</Alert.Text>
        </Alert>
      )}
      <h1>Create a New Company Account</h1>
      <p className="usa-intro padding-bottom-1">
        Please submit your email address to receive an activation link to help ensure a secure enrollment.
      </p>
      <Alert status="info" slim>
        <Alert.Text className="grid-col-9">
          If your company already has an E-Verify account do not enroll again. Contact your account administrator if you
          need to create a new user account or reactivate an existing user account.
        </Alert.Text>
      </Alert>
      {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
      <form id="new-user-form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <TextInputControl
          id="email-address"
          name="emailAddress"
          label="Email Address"
          className="grid-col-9 tablet:grid-col-3"
          type="email"
          required
          ref={register({
            required: 'Email address is required.',
            pattern: {
              value: emailValidationRegex,
              message: 'Please enter a valid email address.',
            },
          })}
          errorId="email-address-error"
          errorMessage={errors?.emailAddress?.message}
        />
        <div className="padding-top-3 padding-bottom-2">
          <p>
            Questions? We&apos;re here to help! Email E-Verify support at{' '}
            <a
              href="mailto:E-Verify@uscis.dhs.gov"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Send an email to E-Verify@uscis.dhs.gov"
            >
              E-Verify@uscis.dhs.gov
            </a>
            .
          </p>
        </div>
        {submissionError && (
          <Alert status="error" className="margin-bottom-2">
            <Alert.Text>
              Your request is unable to be completed at this time. Please wait a few minutes and try again.
            </Alert.Text>
          </Alert>
        )}
        <div className="padding-top-3 padding-bottom-4 display-flex flex-row flex-align-center">
          <hr className="width-full" />
        </div>
        <Button form="new-user-form" type="submit" variant="submit" disabled={isLoading}>
          Submit
        </Button>
      </form>
    </section>
  );
};

export default NewUserAccountEve;
